import React from "react";
import { useTranslation } from "react-i18next";
import ReservationTabs from "../../components/reservation-tabs/ReservationTabs";
import ReservationTypesCarousel from "../../components/reservation-types-carousel/ReservationTypesCarousel";
import { RESERVATION_TYPES } from "../../constants";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faBed,
  faCar,
  faCalendarCheck,
  faCircleCheck,
  faCity,
  faCompass,
  faHeadset,
  faHotel,
  faMapLocationDot,
  faMapMarkedAlt,
  faMountainSun,
  faPersonHiking,
  faRoad,
  faRoute,
  faShieldHalved,
  faStar,
  faTag,
  faUserGroup,
  faVanShuttle,
} from "@fortawesome/free-solid-svg-icons";
import { nowInCET, getDateInServerExpectedFormat } from "../../utils/helpers";
import { getCityDualDisplayName } from "../../utils/tripDisplay";
import SeoHead from "../../components/seo/SeoHead";
import { getSiteUrl } from "../../components/seo/siteUrl";

/* ================================================================
   UNSPLASH IMAGE URLS (free, CDN-hosted)
   ================================================================ */
const IMG = {
  hotel:
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80",
  villa:
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
  apartment:
    "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=80",
  jablanica:
    "https://images.unsplash.com/photo-1691591066243-ed6eca4faec3?w=1200&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8amFibGFuaWNhfGVufDB8fDB8fHww",
  travnik:
    "https://visitbih.ba/wp-content/uploads/2019/05/VH-Plava-voda-i-Lutvina-kafana-2.jpg",
  vlasic:
    "https://images.unsplash.com/photo-1692528172647-00249925d85d?auto=format&fit=crop&w=800&q=80",
  smallCar:
    "https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?auto=format&fit=crop&w=800&q=80",
  hatchback:
    "https://images.unsplash.com/photo-1572634303017-48a1618ab4fd?auto=format&fit=crop&w=800&q=80",
  suv:
    "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?auto=format&fit=crop&w=800&q=80",
  van:
    "https://images.unsplash.com/photo-1455275803899-34511e680e27?auto=format&fit=crop&w=800&q=80",
  limo:
    "https://images.unsplash.com/photo-1571727798577-340250f48c15?auto=format&fit=crop&w=800&q=80",
  minibus:
    "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=800&q=80",
  caravan:
    "https://images.unsplash.com/photo-1758218921124-dcb02283f206?auto=format&fit=crop&w=800&q=80",
  monoVolumen:
    "https://images.unsplash.com/photo-1632848799492-fdfd8a392642?auto=format&fit=crop&w=800&q=80",
};

/* ================================================================
   HOME PAGE DATA (copy + images; strings from i18n home namespace)
   ================================================================ */
const HOME_DESTINATIONS = [
  { id: "sarajevo", city: "Sarajevo", image: "/sarajevo.jpg" },
  { id: "mostar", city: "Mostar", image: "/mostar.jpg" },
  { id: "bihac", city: "Bihac", image: "/bihac.jpg" },
  { id: "jajce", city: "Jajce", image: "/jajce.jpg" },
];

const HOME_CAR_CARDS = [
  { cardKey: "compact", vehicleType: "small_car", image: "/fleet.jpg", badgeColor: "teal" },
  { cardKey: "family", vehicleType: "suv", image: "/road.jpg", badgeColor: "orange" },
  { cardKey: "premium", vehicleType: "limo", image: "/sarajevo-2.jpg", badgeColor: "purple" },
];

const TRUST_ITEMS = [
  { icon: faTag, tKey: "bestPrices" },
  { icon: faShieldHalved, tKey: "secureBooking" },
  { icon: faHeadset, tKey: "support" },
  { icon: faStar, tKey: "verifiedHosts" },
];

/* ================================================================
   /stays PAGE DATA
   ================================================================ */
const ACCOMMODATION_TYPES = [
  { type: "hotel", image: IMG.hotel },
  { type: "villa", image: IMG.villa },
  { type: "apartment", image: IMG.apartment },
];

const ALL_STAY_CITIES = [
  { id: "sarajevo", city: "Sarajevo", image: "/sarajevo.jpg" },
  { id: "mostarStay", city: "Mostar", image: "/mostar.jpg" },
  { id: "jajceStay", city: "Jajce", image: "/jajce.jpg" },
  { id: "jablanica", city: "Jablanica", image: IMG.jablanica },
  { id: "bihacStay", city: "Bihac", image: "/bihac.jpg" },
  { id: "travnik", city: "Travnik", image: IMG.travnik },
  { id: "vlasic", city: "Vlasic", image: IMG.vlasic },
];

/* ================================================================
   /cars PAGE DATA
   ================================================================ */
const VEHICLE_TYPES = [
  { type: "small_car", image: IMG.smallCar },
  { type: "hatchback", image: IMG.hatchback },
  { type: "suv", image: IMG.suv },
  { type: "van", image: IMG.van },
  { type: "limo", image: IMG.limo },
  { type: "minibus", image: IMG.minibus },
  { type: "caravan", image: IMG.caravan },
  { type: "mono_volumen", image: IMG.monoVolumen },
];

const YEAR_CATEGORIES = [
  {
    tKey: "yearNew",
    key: "new",
    gradient: "linear-gradient(135deg, #38B2AC 0%, #2C7A7B 50%, #234E52 100%)",
    iconColor: "rgba(255,255,255,0.15)",
    sparkle: "\u2728",
  },
  {
    tKey: "yearMid",
    key: "mid",
    gradient: "linear-gradient(135deg, #4299E1 0%, #3182CE 50%, #2B6CB0 100%)",
    iconColor: "rgba(255,255,255,0.15)",
    sparkle: "\u2B50",
  },
  {
    tKey: "yearEconomy",
    key: "economy",
    gradient: "linear-gradient(135deg, #ED8936 0%, #DD6B20 50%, #C05621 100%)",
    iconColor: "rgba(255,255,255,0.15)",
    sparkle: "\uD83D\uDCB0",
  },
];

/* ================================================================
   /tours PAGE DATA
   ================================================================ */
const TOUR_CITIES = [
  { id: "tourSarajevo", city: "Sarajevo", image: "/sarajevo-2.jpg" },
  { id: "tourMostar", city: "Mostar", image: "/mostar-card.jpg" },
  { id: "tourBihac", city: "Bihac", image: "/bihac.jpg" },
  { id: "tourJajce", city: "Jajce", image: "/jajce.jpg" },
];

const HOW_IT_WORKS = [
  { step: "1", icon: faCalendarCheck, tKey: "step1" },
  { step: "2", icon: faCity, tKey: "step2" },
  { step: "3", icon: faBed, tKey: "step3" },
  { step: "4", icon: faCompass, tKey: "step4" },
];

const TRENDING_SEARCHES = [
  { city: "Sarajevo", type: "hotel" },
  { city: "Mostar", type: "villa" },
  { city: "Bihac", type: "apartment" },
  { city: "Jajce", type: "hotel" },
  { city: "Travnik", type: "villa" },
  { city: "Sarajevo", type: "apartment" },
  { city: "Mostar", type: "hotel" },
  { city: "Vlasic", type: "villa" },
];

const TOUR_FEATURES = [
  { icon: faVanShuttle, tKey: "featDriver" },
  { icon: faBed, tKey: "featStays" },
  { icon: faRoute, tKey: "featItinerary" },
  { icon: faUserGroup, tKey: "featGroup" },
  { icon: faMountainSun, tKey: "featScenic" },
  { icon: faPersonHiking, tKey: "featLocal" },
];

/* ================================================================
   REUSABLE CARD COMPONENT
   ================================================================ */
function DestinationCard({ to, image, badge, badgeColor, title, tagline, description }) {
  return (
    <Box
      as={RouterLink}
      to={to}
      borderRadius="xl"
      overflow="hidden"
      bg="white"
      shadow="md"
      transition="all 0.3s"
      _hover={{ transform: "translateY(-6px)", shadow: "2xl", textDecoration: "none" }}
      role="group"
    >
      <Box position="relative" overflow="hidden">
        <Image
          src={image}
          alt={title}
          w="100%"
          h="180px"
          objectFit="cover"
          transition="transform 0.4s"
          _groupHover={{ transform: "scale(1.08)" }}
        />
        {badge && (
          <Badge
            position="absolute"
            top={3}
            colorScheme={badgeColor || "teal"}
            borderRadius="full"
            px={3}
            py={1}
            fontSize="xs"
            sx={{ insetInlineStart: 3 }}
          >
            {badge}
          </Badge>
        )}
      </Box>
      <Box p={4}>
        <Heading size="md" color="gray.800" mb={1}>
          {title}
        </Heading>
        {tagline && (
          <Text fontSize="sm" fontWeight="600" color="teal.600" mb={2}>
            {tagline}
          </Text>
        )}
        <Text fontSize="sm" color="gray.600" noOfLines={3}>
          {description}
        </Text>
      </Box>
    </Box>
  );
}

function HeroBanner({ bgImage, children }) {
  return (
    <Box
      w="full"
      borderRadius="2xl"
      overflow="hidden"
      position="relative"
      minH={{ base: "340px", md: "360px" }}
      bgImage={`url('${bgImage}')`}
      bgSize="cover"
      bgPosition="center"
    >
      <Box
        position="absolute"
        inset="0"
        bg="linear-gradient(to bottom, rgba(0,128,128,0.5) 0%, rgba(0,0,0,0.6) 100%)"
      />
      <Flex
        position="relative"
        zIndex={1}
        direction="column"
        align="center"
        justify="center"
        textAlign="center"
        color="white"
        h="full"
        minH={{ base: "340px", md: "360px" }}
        px={{ base: 6, md: 8 }}
        py={{ base: 10, md: 12 }}
        gap={{ base: 1, md: 0 }}
      >
        {children}
      </Flex>
    </Box>
  );
}

function SectionHeading({ title, subtitle, icon }) {
  const { i18n } = useTranslation("home");
  const isRtl = i18n.dir() === "rtl";
  return (
    <VStack align="start" spacing={1} mb={6}>
      <HStack spacing={2} flexDir={isRtl ? "row-reverse" : "row"} alignItems="center">
        <Heading size="lg" color="gray.800">
          {title}
        </Heading>
        {icon && <FontAwesomeIcon icon={icon} color="#319795" />}
      </HStack>
      {subtitle && (
        <Text color="gray.500" fontSize="md">
          {subtitle}
        </Text>
      )}
    </VStack>
  );
}

/* ================================================================
   HOME COMPONENT
   ================================================================ */
function Home({ selectedTab = RESERVATION_TYPES.STAY }) {
  const { t, i18n } = useTranslation(["home", "seo"]);
  const isRtl = i18n.dir() === "rtl";
  const nextArrow = isRtl ? faArrowLeft : faArrowRight;
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const isHome = path === "/";
  const isStay = path.startsWith("/stays");
  const isCar = path.startsWith("/car");
  const isTour = path.startsWith("/tour");

  const seoKey = isStay
    ? "stays"
    : isCar
      ? "cars"
      : isTour
        ? "tours"
        : "default";
  const seoTitle = t(`seo:${seoKey}Title`);
  const seoDescription = t(`seo:${seoKey}Description`);
  const siteUrl = getSiteUrl();
  const homeJsonLd = isHome
    ? {
        "@context": "https://schema.org",
        "@type": "TravelAgency",
        name: "Fursatook",
        url: siteUrl,
        logo: `${siteUrl}/fursatook-logo.jpg`,
        description: t("seo:travelAgencyDescription"),
        areaServed: {
          "@type": "Country",
          name: "Bosnia and Herzegovina",
        },
      }
    : undefined;

  const today = nowInCET();
  const tenDaysLater = new Date(today);
  tenDaysLater.setDate(today.getDate() + 10);

  const sd = getDateInServerExpectedFormat(today);
  const ed = getDateInServerExpectedFormat(tenDaysLater);

  const baseStay = `start_date=${sd}&end_date=${ed}&adults=2&rooms=1`;
  const baseCar = `location=${encodeURIComponent("Sarajevo Intl Airport SJJ \u2708")}&start_date=${sd}&end_date=${ed}&pick_up_time=08%3A30&drop_off_time=18%3A00&adults=2&rooms=1`;
  const baseTour = `start_date=${sd}&end_date=${ed}&adults=2`; // &rooms=1 — omitted until tour stay picker needs it

  const stayUrl = (city, type) => {
    let url = `/stays/search?location=${encodeURIComponent(city)}&${baseStay}`;
    if (type) url += `&types=${type}`;
    return url;
  };

  const carUrl = (type, yearCat) => {
    let url = `/cars/search?${baseCar}`;
    if (type) url += `&types=${type}`;
    if (yearCat) url += `&production_year_category=${yearCat}`;
    return url;
  };

  const tourUrl = () => `/tours/builder?${baseTour}`;

  const tourHighlights = t("toursBanner.highlights", { returnObjects: true }) || [];
  const trendingLabels = t("stays.trending", { returnObjects: true }) || [];

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        path={path}
        jsonLd={homeJsonLd}
      />
      <ReservationTabs selectedTab={selectedTab} />

      {isHome && (
        <Box w="full" mt={{ base: 4, md: 6 }} mb={{ base: 2, md: 4 }}>
          <ReservationTypesCarousel />
        </Box>
      )}

      {/* ==================== HOME PAGE ==================== */}
      {isHome && (
        <VStack spacing={{ base: 8, md: 12 }} w="full" pb={10}>
          {/* Popular Destinations */}
          <Box w="full">
            <SectionHeading
              title={t("destinations.sectionTitle")}
              subtitle={t("destinations.sectionSubtitle")}
            />
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
              {HOME_DESTINATIONS.map((d) => (
                <DestinationCard
                  key={d.city}
                  to={stayUrl(d.city)}
                  image={d.image}
                  badge={t(`destinations.${d.id}.properties`)}
                  title={getCityDualDisplayName(d.city, t, i18n.language)}
                  tagline={t(`destinations.${d.id}.tagline`)}
                  description={t(`destinations.${d.id}.description`)}
                />
              ))}
            </SimpleGrid>
          </Box>

          {/* Car Rentals */}
          <Box w="full">
            <SectionHeading
              title={t("carRentals.sectionTitle")}
              icon={faCar}
              subtitle={t("carRentals.sectionSubtitle")}
            />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
              {HOME_CAR_CARDS.map((car) => (
                <Card
                  key={car.cardKey}
                  as={RouterLink}
                  to={carUrl(car.vehicleType)}
                  borderRadius="xl"
                  overflow="hidden"
                  shadow="md"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-6px)", shadow: "2xl", textDecoration: "none" }}
                >
                  <Box position="relative" overflow="hidden">
                    <Image
                      src={car.image}
                      alt={t(`carRentals.${car.cardKey}.title`)}
                      w="100%"
                      h="180px"
                      objectFit="cover"
                    />
                    <Badge
                      position="absolute"
                      top={3}
                      colorScheme={car.badgeColor}
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      sx={{ insetInlineEnd: 3 }}
                    >
                      {t(`carRentals.${car.cardKey}.badge`)}
                    </Badge>
                  </Box>
                  <CardBody>
                    <Stack spacing={2}>
                      <Heading size="md">{t(`carRentals.${car.cardKey}.title`)}</Heading>
                      <Text fontSize="sm" color="gray.600" noOfLines={3}>
                        {t(`carRentals.${car.cardKey}.description`)}
                      </Text>
                      <Text color="teal.600" fontSize="xl" fontWeight="bold">
                        {t(`carRentals.${car.cardKey}.price`)}
                      </Text>
                    </Stack>
                  </CardBody>
                  <Divider color="gray.100" />
                  <CardFooter>
                    <Button colorScheme="teal" variant="outline" w="full" pointerEvents="none">
                      {t(`carRentals.${car.cardKey}.browse`)}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </SimpleGrid>
          </Box>

          {/* Tours CTA Banner */}
          <Box
            w="full"
            borderRadius="2xl"
            overflow="hidden"
            position="relative"
            minH={{ base: "auto", md: "360px" }}
            bgImage="url('/tour-bg.jpg')"
            bgSize="cover"
            bgPosition="center"
          >
            <Box
              position="absolute"
              inset="0"
              bg="linear-gradient(135deg, rgba(0,128,128,0.7) 0%, rgba(0,0,0,0.55) 100%)"
            />
            <Flex
              position="relative"
              zIndex={1}
              direction={{ base: "column", md: "row" }}
              align="center"
              justify="space-between"
              px={{ base: 6, md: 10 }}
              py={{ base: 8, md: 10 }}
              gap={8}
            >
              <VStack
                align={{ base: "center", md: "start" }}
                spacing={4}
                color="white"
                flex={1}
                textAlign={{ base: "center", md: isRtl ? "right" : "left" }}
              >
                <Badge fontSize="sm" px={3} py={1} borderRadius="full" color="white" bg="whiteAlpha.300">
                  <FontAwesomeIcon icon={faMapLocationDot} />
                  &nbsp; {t("toursBanner.badge")}
                </Badge>
                <Heading fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }} fontWeight="800">
                  {t("toursBanner.title")}
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} opacity={0.95} maxW="xl">
                  {t("toursBanner.description")}
                </Text>
                <Button
                  size="lg"
                  colorScheme="teal"
                  onClick={() => navigate(tourUrl())}
                  leftIcon={isRtl ? <FontAwesomeIcon icon={nextArrow} /> : undefined}
                  rightIcon={!isRtl ? <FontAwesomeIcon icon={nextArrow} /> : undefined}
                  _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                  transition="all 0.2s"
                  mt={2}
                >
                  {t("toursBanner.discoverTours")}
                </Button>
              </VStack>
              <VStack
                bg="whiteAlpha.200"
                backdropFilter="blur(8px)"
                borderRadius="xl"
                p={6}
                spacing={4}
                minW={{ base: "full", md: "260px" }}
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
              >
                <Text fontWeight="700" fontSize="lg">{t("toursBanner.highlightsTitle")}</Text>
                {tourHighlights.map((h) => (
                  <HStack key={h} spacing={3} w="full">
                    <FontAwesomeIcon icon={faCircleCheck} color="#81E6D9" />
                    <Text fontSize="sm">{h}</Text>
                  </HStack>
                ))}
              </VStack>
            </Flex>
          </Box>

          {/* Why Book With Us */}
          <Box w="full">
            <Heading size="lg" color="gray.800" mb={6} textAlign="center">
              {t("trust.title")}
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              {TRUST_ITEMS.map((item) => (
                <VStack
                  key={item.tKey}
                  bg="white"
                  p={6}
                  borderRadius="xl"
                  shadow="sm"
                  spacing={3}
                  textAlign="center"
                  border="1px solid"
                  borderColor="gray.100"
                  transition="all 0.2s"
                  _hover={{ shadow: "md", borderColor: "teal.200" }}
                >
                  <Flex w={12} h={12} borderRadius="full" bg="teal.50" align="center" justify="center">
                    <FontAwesomeIcon icon={item.icon} color="#319795" fontSize="20px" />
                  </Flex>
                  <Text fontWeight="700" color="gray.800">{t(`trust.${item.tKey}.title`)}</Text>
                  <Text fontSize="sm" color="gray.500">{t(`trust.${item.tKey}.desc`)}</Text>
                </VStack>
              ))}
            </SimpleGrid>
          </Box>

          {/* Bottom CTA */}
          <Box w="full" bg="teal.50" borderRadius="2xl" p={{ base: 6, md: 10 }} textAlign="center">
            <Heading size="md" color="teal.800" mb={2}>
              {t("bottomCta.title")}
            </Heading>
            <Text color="teal.600" mb={6} maxW="lg" mx="auto">
              {t("bottomCta.description")}
            </Text>
            <HStack justify="center" spacing={4} flexWrap="wrap">
              <Button
                colorScheme="teal"
                size="md"
                onClick={() => navigate(stayUrl("Sarajevo"))}
                leftIcon={!isRtl ? <FontAwesomeIcon icon={faBed} /> : undefined}
                rightIcon={isRtl ? <FontAwesomeIcon icon={faBed} /> : undefined}
              >
                {t("bottomCta.browseStays")}
              </Button>
              <Button
                colorScheme="teal"
                variant="outline"
                size="md"
                onClick={() => navigate(carUrl())}
                leftIcon={!isRtl ? <FontAwesomeIcon icon={faCar} /> : undefined}
                rightIcon={isRtl ? <FontAwesomeIcon icon={faCar} /> : undefined}
              >
                {t("bottomCta.rentCar")}
              </Button>
              <Button
                colorScheme="teal"
                variant="outline"
                size="md"
                onClick={() => navigate(tourUrl())}
                leftIcon={!isRtl ? <FontAwesomeIcon icon={faMapLocationDot} /> : undefined}
                rightIcon={isRtl ? <FontAwesomeIcon icon={faMapLocationDot} /> : undefined}
              >
                {t("bottomCta.planTour")}
              </Button>
            </HStack>
          </Box>
        </VStack>
      )}

      {/* ==================== STAYS PAGE ==================== */}
      {isStay && (
        <VStack spacing={{ base: 8, md: 12 }} w="full" pb={10}>
          {/* Hero */}
          <HeroBanner bgImage="/mostar-card-2.jpg">
            <Badge fontSize="sm" px={3} py={1} borderRadius="full" color="white" bg="whiteAlpha.300" mb={3}>
              <FontAwesomeIcon icon={faBed} /> &nbsp; {t("stays.heroBadge")}
            </Badge>
            <Heading
              fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
              fontWeight="800"
              mb={1}
              textShadow="0 2px 12px rgba(0,0,0,0.3)"
            >
              {t("stays.heroTitle")}
            </Heading>
            <Text fontSize={{ base: "md", md: "xl" }} maxW="2xl" mb={6} opacity={0.95}>
              {t("stays.heroSubtitle")}
            </Text>
            <Button
              colorScheme="teal"
              size="lg"
              onClick={() => navigate(stayUrl("Sarajevo"))}
              _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
              transition="all 0.2s"
              py={3}
            >
              {t("stays.searchAll")}
            </Button>
          </HeroBanner>

          {/* Browse by Property Type */}
          <Box w="full">
            <SectionHeading
              title={t("stays.browseByTypeTitle")}
              icon={faHotel}
              subtitle={t("stays.browseByTypeSubtitle")}
            />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
              {ACCOMMODATION_TYPES.map((acc) => (
                <Card
                  key={acc.type}
                  as={RouterLink}
                  to={stayUrl("Sarajevo", acc.type)}
                  borderRadius="xl"
                  overflow="hidden"
                  shadow="md"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-6px)", shadow: "2xl", textDecoration: "none" }}
                  role="group"
                >
                  <Box position="relative" overflow="hidden">
                    <Image
                      src={acc.image}
                      alt={t(`stays.${acc.type}.label`)}
                      w="100%"
                      h="200px"
                      objectFit="cover"
                      transition="transform 0.4s"
                      _groupHover={{ transform: "scale(1.06)" }}
                    />
                    <Badge
                      position="absolute"
                      top={3}
                      colorScheme="teal"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      sx={{ insetInlineEnd: 3 }}
                    >
                      {t(`stays.${acc.type}.badge`)}
                    </Badge>
                  </Box>
                  <CardBody>
                    <Heading size="md" mb={2}>{t(`stays.${acc.type}.label`)}</Heading>
                    <Text fontSize="sm" color="gray.600">{t(`stays.${acc.type}.description`)}</Text>
                  </CardBody>
                  <CardFooter pt={0}>
                    <Button
                      colorScheme="teal"
                      variant="ghost"
                      size="sm"
                      rightIcon={<FontAwesomeIcon icon={nextArrow} />}
                      pointerEvents="none"
                    >
                      {t(`stays.${acc.type}.explore`)}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </SimpleGrid>
          </Box>

          {/* Explore All Destinations */}
          <Box w="full">
            <SectionHeading
              title={t("stays.exploreDestinationsTitle")}
              icon={faMapMarkedAlt}
              subtitle={t("stays.exploreDestinationsSubtitle")}
            />
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
              {ALL_STAY_CITIES.map((d) => (
                <DestinationCard
                  key={d.city}
                  to={stayUrl(d.city)}
                  image={d.image}
                  title={getCityDualDisplayName(d.city, t, i18n.language)}
                  tagline={t(`stays.${d.id}.tagline`)}
                  description={t(`stays.${d.id}.description`)}
                />
              ))}
            </SimpleGrid>
          </Box>

          {/* Trending Searches */}
          <Box w="full" bg="teal.50" borderRadius="2xl" p={{ base: 6, md: 8 }}>
            <Heading size="md" color="teal.800" mb={4}>
              {t("stays.trendingTitle")}
            </Heading>
            <Flex gap={3} flexWrap="wrap">
              {TRENDING_SEARCHES.map((item, idx) => (
                <Button
                  key={`${item.city}-${item.type}-${idx}`}
                  as={RouterLink}
                  to={stayUrl(item.city, item.type)}
                  size="sm"
                  variant="outline"
                  colorScheme="teal"
                  borderRadius="full"
                  _hover={{ bg: "teal.500", color: "white" }}
                  transition="all 0.2s"
                >
                  {trendingLabels[idx]}
                </Button>
              ))}
            </Flex>
          </Box>
        </VStack>
      )}

      {/* ==================== CARS PAGE ==================== */}
      {isCar && (
        <VStack spacing={{ base: 8, md: 12 }} w="full" pb={10}>
          {/* Hero */}
          <Box
            w="full"
            borderRadius="2xl"
            overflow="hidden"
            position="relative"
            minH={{ base: "260px", md: "320px" }}
            bgImage="url('/bosnia-photo.jpg')"
            bgSize="cover"
            bgPosition="center"
          >
            <Box
              position="absolute"
              inset="0"
              bg="linear-gradient(135deg, rgba(0,128,128,0.65) 0%, rgba(0,0,0,0.55) 100%)"
            />
            <Flex
              position="relative"
              zIndex={1}
              direction="column"
              align="center"
              justify="center"
              textAlign="center"
              color="white"
              minH={{ base: "260px", md: "320px" }}
              px={{ base: 6, md: 8 }}
              py={{ base: 7, md: 9 }}
              gap={{ base: 2, md: 3 }}
            >
              <FontAwesomeIcon icon={faVanShuttle} fontSize="32px" style={{ opacity: 0.9 }} />
              <Heading fontSize={{ base: "2xl", md: "4xl" }} fontWeight="800" textShadow="0 2px 12px rgba(0,0,0,0.3)">
                {t("cars.roadTripTitle")}
              </Heading>
              <Text fontSize={{ base: "md", md: "xl" }} maxW="2xl" opacity={0.95}>
                {t("cars.roadTripSubtitle")}
              </Text>
              <Button
                colorScheme="teal"
                size="lg"
                onClick={() => navigate(carUrl())}
                _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                transition="all 0.2s"
                py={3}
              >
                {t("cars.searchAll")}
              </Button>
            </Flex>
          </Box>

          {/* Vehicle Categories */}
          <Box w="full">
            <SectionHeading
              title={t("cars.chooseRideTitle")}
              icon={faCar}
              subtitle={t("cars.chooseRideSubtitle")}
            />
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
              {VEHICLE_TYPES.map((v) => (
                <Card
                  key={v.type}
                  as={RouterLink}
                  to={carUrl(v.type)}
                  borderRadius="xl"
                  overflow="hidden"
                  shadow="md"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-6px)", shadow: "2xl", textDecoration: "none" }}
                  role="group"
                >
                  <Box position="relative" overflow="hidden">
                    <Image
                      src={v.image}
                      alt={t(`cars.${v.type}.label`)}
                      w="100%"
                      h="160px"
                      objectFit="cover"
                      transition="transform 0.4s"
                      _groupHover={{ transform: "scale(1.06)" }}
                    />
                    <Badge
                      position="absolute"
                      bottom={3}
                      bg="blackAlpha.700"
                      color="white"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      sx={{ insetInlineStart: 3 }}
                    >
                      {t(`cars.${v.type}.seats`)}
                    </Badge>
                  </Box>
                  <CardBody>
                    <Heading size="sm" mb={1}>{t(`cars.${v.type}.label`)}</Heading>
                    <Text fontSize="xs" color="gray.600" noOfLines={2}>
                      {t(`cars.${v.type}.description`)}
                    </Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>

          {/* Browse by Year */}
          <Box w="full">
            <SectionHeading
              title={t("cars.fleetAgeTitle")}
              icon={faRoad}
              subtitle={t("cars.fleetAgeSubtitle")}
            />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
              {YEAR_CATEGORIES.map((yr) => (
                <Box
                  key={yr.key}
                  as={RouterLink}
                  to={carUrl(null, yr.key)}
                  borderRadius="xl"
                  overflow="hidden"
                  shadow="md"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-6px)", shadow: "2xl", textDecoration: "none" }}
                  role="group"
                >
                  <Box
                    bg={yr.gradient}
                    position="relative"
                    h="160px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      fontSize="140px"
                      color={yr.iconColor}
                      transform="rotate(-12deg)"
                      bottom="-20px"
                      transition="transform 0.4s"
                      _groupHover={{ transform: "rotate(-8deg) scale(1.1)" }}
                      sx={{ insetInlineEnd: "-10px" }}
                    >
                      <FontAwesomeIcon icon={faCar} />
                    </Box>
                    <VStack spacing={1} position="relative" zIndex={1} color="white" textAlign="center">
                      <Text fontSize="3xl">{yr.sparkle}</Text>
                      <Heading size="lg" textShadow="0 2px 8px rgba(0,0,0,0.2)">
                        {t(`cars.${yr.tKey}.label`)}
                      </Heading>
                      <Badge
                        bg="whiteAlpha.300"
                        color="white"
                        borderRadius="full"
                        px={3}
                        py={1}
                        fontSize="xs"
                      >
                        {t(`cars.${yr.tKey}.subtitle`)}
                      </Badge>
                    </VStack>
                  </Box>
                  <Box bg="white" p={4}>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      {t(`cars.${yr.tKey}.description`)}
                    </Text>
                    <Button
                      colorScheme="teal"
                      variant="ghost"
                      size="sm"
                      rightIcon={<FontAwesomeIcon icon={nextArrow} />}
                      pointerEvents="none"
                    >
                      {t(`cars.${yr.tKey}.browse`)}
                    </Button>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </VStack>
      )}

      {/* ==================== TOURS PAGE ==================== */}
      {isTour && (
        <VStack spacing={{ base: 8, md: 12 }} w="full" pb={10}>
          {/* Hero */}
          <HeroBanner bgImage="/jajce.jpg">
            <Badge fontSize="sm" px={3} py={1} borderRadius="full" color="white" bg="whiteAlpha.300" mb={3}>
              <FontAwesomeIcon icon={faRoute} /> &nbsp; {t("tours.heroBadge")}
            </Badge>
            <Heading
              fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
              fontWeight="800"
              mb={3}
              textShadow="0 2px 12px rgba(0,0,0,0.3)"
            >
              {t("tours.heroTitle")}
            </Heading>
            <Text fontSize={{ base: "md", md: "xl" }} maxW="2xl" mb={6} opacity={0.95}>
              {t("tours.heroSubtitle")}
            </Text>
            <Button
              colorScheme="teal"
              size="lg"
              onClick={() => navigate(tourUrl())}
              rightIcon={<FontAwesomeIcon icon={nextArrow} />}
              _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
              transition="all 0.2s"
            >
              {t("tours.buildTour")}
            </Button>
          </HeroBanner>

          {/* How It Works */}
          <Box w="full">
            <Heading size="lg" color="gray.800" mb={6} textAlign="center">
              {t("tours.howItWorksTitle")}
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              {HOW_IT_WORKS.map((step) => (
                <VStack key={step.step} spacing={3} textAlign="center">
                  <Flex
                    w={16}
                    h={16}
                    borderRadius="full"
                    bg="teal.500"
                    color="white"
                    align="center"
                    justify="center"
                    position="relative"
                  >
                    <FontAwesomeIcon icon={step.icon} fontSize="24px" />
                    <Badge
                      position="absolute"
                      top={-1}
                      colorScheme="teal"
                      borderRadius="full"
                      fontSize="xs"
                      w={6}
                      h={6}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ insetInlineEnd: -1, top: -1 }}
                    >
                      {step.step}
                    </Badge>
                  </Flex>
                  <Text fontWeight="700" color="gray.800">{t(`tours.${step.tKey}.title`)}</Text>
                  <Text fontSize="sm" color="gray.500">{t(`tours.${step.tKey}.description`)}</Text>
                </VStack>
              ))}
            </SimpleGrid>
          </Box>

          {/* Tour City Cards */}
          <Box w="full">
            <SectionHeading
              title={t("tours.citiesTitle")}
              icon={faMapLocationDot}
              subtitle={t("tours.citiesSubtitle")}
            />
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
              {TOUR_CITIES.map((d) => (
                <DestinationCard
                  key={d.city}
                  to={tourUrl()}
                  image={d.image}
                  title={getCityDualDisplayName(d.city, t, i18n.language)}
                  tagline={t(`tours.${d.id}.tagline`)}
                  description={t(`tours.${d.id}.description`)}
                />
              ))}
            </SimpleGrid>
          </Box>

          {/* Tour Features */}
          <Box w="full">
            <Heading size="lg" color="gray.800" mb={6} textAlign="center">
              {t("tours.includedTitle")}
            </Heading>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6}>
              {TOUR_FEATURES.map((feat) => (
                <HStack
                  key={feat.tKey}
                  bg="white"
                  p={5}
                  borderRadius="xl"
                  shadow="sm"
                  spacing={4}
                  align="start"
                  border="1px solid"
                  borderColor="gray.100"
                  transition="all 0.2s"
                  flexDir={isRtl ? "row-reverse" : "row"}
                  _hover={{ shadow: "md", borderColor: "teal.200" }}
                >
                  <Flex w={10} h={10} borderRadius="lg" bg="teal.50" align="center" justify="center" flexShrink={0}>
                    <FontAwesomeIcon icon={feat.icon} color="#319795" />
                  </Flex>
                  <Box>
                    <Text fontWeight="700" color="gray.800" mb={1}>{t(`tours.${feat.tKey}.title`)}</Text>
                    <Text fontSize="sm" color="gray.500">{t(`tours.${feat.tKey}.desc`)}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          {/* CTA */}
          <Box w="full" bg="teal.50" borderRadius="2xl" p={{ base: 6, md: 10 }} textAlign="center">
            <Heading size="lg" color="teal.800" mb={2}>
              {t("tours.ctaTitle")}
            </Heading>
            <Text color="teal.600" mb={6} maxW="lg" mx="auto">
              {t("tours.ctaSubtitle")}
            </Text>
            <Button
              colorScheme="teal"
              size="lg"
              onClick={() => navigate(tourUrl())}
              rightIcon={<FontAwesomeIcon icon={nextArrow} />}
              _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
              transition="all 0.2s"
            >
              {t("tours.ctaButton")}
            </Button>
          </Box>
        </VStack>
      )}
    </>
  );
}

export default Home;
